import { profilesConstants, SYNC_BADGE, SYNC_TYPE } from '@constants/profiles';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import { getProfileSyncBadge, isScannerProfile } from '@helpers/profileSync';
import { profilesService } from '@services';

function request() {
    return { type: profilesConstants.PROFILES_FETCH };
}

function success(data) {
    return { type: profilesConstants.PROFILES_SUCCESS, data };
}

function failure(message) {
    return { type: profilesConstants.PROFILES_ERROR, message };
}

const get = (query, page = 0, pageSize = DEFAULT_PAGE_SIZE) => {
    return dispatch => {
        dispatch(request());
        profilesService.get(query, page, pageSize).then(
            data => dispatch(success(data)),
            () => dispatch(failure()),
        );
    };
};

// Skips PROFILES_FETCH so the polling loop doesn't flash the skeleton on every tick.
const getSilent = (query, page = 0, pageSize = DEFAULT_PAGE_SIZE) => {
    return dispatch => {
        profilesService.get(query, page, pageSize).then(
            data => dispatch(success(data)),
            () => dispatch(failure()),
        );
    };
};

function successFilterData(data) {
    return { type: profilesConstants.PROFILES_FILTER_SUCCESS, data };
}

function failureFilterData() {
    return { type: profilesConstants.PROFILES_FILTER_ERROR };
}

const getFilterData = () => {
    return dispatch => {
        profilesService.getFilterData().then(
            data => dispatch(successFilterData(data)),
            () => dispatch(failureFilterData()),
        );
    };
};

const setSyncStatus = data => {
    return { type: profilesConstants.SET_SYNC_STATUS, data };
};

const setProfiles = data => {
    return { type: profilesConstants.SET_PROFILES, data };
};

const updateProfile = updatedProfile => (dispatch, getState) => {
    if (!updatedProfile?.profileId) return;

    const data = getState().profiles.data.map(p => (p.profileId === updatedProfile.profileId ? updatedProfile : p));
    dispatch(setProfiles(data));
};

// Mirrors the backend publish-handover override (non-Synced scanner rows render as IN_PROGRESS
// once the location is publishing) so the user sees the badge flip on click instead of after
// the API round trip. Updates rows and aggregate counts in one dispatch — keeps the donut chart
// in sync with the table. The follow-up refetch reconciles to authoritative state.
const markLocationPublishing = locationId => (dispatch, getState) => {
    const state = getState().profiles;

    let conflictDelta = 0;
    let notPublishedDelta = 0;
    let naDelta = 0;
    let syncedWithConflictsDelta = 0;
    let inProgressDelta = 0;

    const data = state.data.map(p => {
        if (p.locationId !== locationId || !isScannerProfile(p)) return p;
        if (p.syncStatus === SYNC_TYPE.SYNCED) return p;
        if (p.syncStatus === SYNC_TYPE.IN_PROGRESS) {
            return p.isLocationPublishing ? p : { ...p, isLocationPublishing: true };
        }

        if (p.syncStatus === SYNC_TYPE.SYNCED_WITH_CONFLICTS) {
            syncedWithConflictsDelta--;
        } else {
            switch (getProfileSyncBadge(p)) {
                case SYNC_BADGE.CONFLICT:
                    conflictDelta--;
                    break;
                case SYNC_BADGE.NOT_FOUND:
                case SYNC_BADGE.NOT_VERIFIED:
                    notPublishedDelta--;
                    break;
                case SYNC_BADGE.DISABLED:
                    naDelta--;
                    break;
                default:
                    break;
            }
        }
        inProgressDelta++;
        return { ...p, isLocationPublishing: true, syncStatus: SYNC_TYPE.IN_PROGRESS };
    });

    dispatch({
        type: profilesConstants.MARK_LOCATION_PUBLISHING,
        data,
        counts: {
            conflictCount: state.conflictCount + conflictDelta,
            notPublishedCount: state.notPublishedCount + notPublishedDelta,
            inProgressCount: state.inProgressCount + inProgressDelta,
            naCount: state.naCount + naDelta,
            syncedWithConflictsCount: state.syncedWithConflictsCount + syncedWithConflictsDelta,
        },
    });
};

export const profilesActions = {
    get,
    getSilent,
    getFilterData,
    setSyncStatus,
    setProfiles,
    updateProfile,
    markLocationPublishing,
};
